import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getToken } from '@shared/services/api';
import { Card, Badge, Button } from '@shared/components/ui';
import { getStatusTone, toneToBadgeVariant } from '@shared/config/theme';
import {
  Search,
  Users,
  Database,
  HardDrive,
  ChevronLeft,
  ChevronRight,
  Filter,
  AlertTriangle,
} from 'lucide-react';

interface Customer {
  id: string;
  name: string;
  billing_email: string | null;
  plan_name: string;
  plan_amount: number;
  status: string;
  storage_used_gb: number;
  backup_count: number;
  created_at: string;
}

interface CustomersResponse {
  customers: Customer[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export default function CustomerManagement() {
  const { t, i18n } = useTranslation('admin');
  const locale = i18n.resolvedLanguage || 'en';
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [showExpiredMessage, setShowExpiredMessage] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const token = getToken();

  useEffect(() => {
    if (searchParams.get('impersonation_expired') === 'true') {
      setShowExpiredMessage(true);
      setTimeout(() => {
        setShowExpiredMessage(false);
        window.history.replaceState({}, '', '/admin/customers');
      }, 10000);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchCustomers();
  }, [search, statusFilter, page]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      params.append('page', page.toString());
      params.append('limit', '50');

      const res = await fetch(`/api/v1/admin/customers?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data: CustomersResponse = await res.json();
        setCustomers(data.customers);
        setPagination(data.pagination);
        setError(null);
      } else if (res.status === 403) {
        setError(t('customerManagement.errors.accessDenied'));
      } else {
        setError(t('customerManagement.errors.loadFailed'));
      }
    } catch {
      setError(t('customerManagement.errors.connectionFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-700">
        <div>
          <h1 className="app-page-title">{t('customerManagement.title')}</h1>
          <p className="text-text-muted-light dark:text-text-muted-dark font-medium mt-1">
            {t('customerManagement.subtitle')}
          </p>
        </div>

        {showExpiredMessage && (
          <div className="p-4 bg-state-warn-bg-light dark:bg-state-warn-bg-dark border border-state-warn-light/20 dark:border-state-warn-dark/20 rounded-xl flex items-start gap-3">
            <AlertTriangle
              className="text-state-warn-light dark:text-state-warn-dark shrink-0 mt-0.5"
              size={20}
            />
            <div>
              <h3 className="text-sm font-bold text-state-warn-light dark:text-state-warn-dark">
                {t('customerManagement.sessionExpired')}
              </h3>
              <p className="text-sm text-state-warn-light dark:text-state-warn-dark mt-1">
                {t('customerManagement.impersonationEnded')}
              </p>
            </div>
          </div>
        )}

        <Card className="p-4">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <label
                htmlFor="customer-search"
                className="block text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 ml-1"
              >
                {t('customerManagement.search')}
              </label>
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-subtle-light dark:text-text-subtle-dark"
                />
                <input
                  id="customer-search"
                  type="text"
                  placeholder={t('name_email_or_org_id')}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                />
              </div>
            </div>
            <div className="w-full md:w-64">
              <label
                htmlFor="customer-status"
                className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
              >
                {t('customerManagement.status')}
              </label>
              <div className="relative">
                <select
                  id="customer-status"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none appearance-none cursor-pointer"
                >
                  <option value="all">{t('customerManagement.allStatuses')}</option>
                  <option value="active">{t('customerManagement.statuses.active')}</option>
                  <option value="trialing">{t('trialing')}</option>
                  <option value="past_due">{t('past_due')}</option>
                  <option value="canceled">{t('canceled')}</option>
                </select>
                <Filter
                  size={16}
                  className="absolute left-3.5 top-3.5 text-text-subtle-light dark:text-text-subtle-dark"
                />
              </div>
            </div>
            <Button type="submit" className="h-[46px] w-full md:w-auto">
              {t('customerManagement.search')}
            </Button>
          </form>
        </Card>

        {loading ? (
          <div
            role="status"
            aria-label={t('common:status.loading')}
            className="py-20 flex justify-center"
          >
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
          </div>
        ) : error ? (
          <div
            role="alert"
            className="py-20 text-center space-y-4 text-state-danger-light dark:text-state-danger-dark font-bold"
          >
            <p>{error}</p>
            <Button onClick={() => void fetchCustomers()}>{t('common:actions.retry')}</Button>
          </div>
        ) : customers.length === 0 ? (
          <div className="py-20 text-center text-text-subtle-light dark:text-text-subtle-dark">
            <Users size={48} className="mx-auto mb-4 opacity-20" />
            <p className="font-medium">{t('customerManagement.noCustomers')}</p>
          </div>
        ) : (
          <>
            <div className="lg:hidden space-y-3">
              {customers.map((c) => (
                <Card key={c.id} className="p-4 space-y-4">
                  <div className="min-w-0">
                    <Link
                      to={`/admin/customers/${c.id}`}
                      className="font-bold text-slate-900 dark:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded-sm"
                    >
                      {c.name}
                    </Link>
                    <p className="text-xs text-text-muted-light dark:text-text-muted-dark break-all">
                      {c.billing_email}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 border-y border-surface-border-light dark:border-surface-border-dark py-3">
                    <div className="min-w-0">
                      <p className="text-[10px] font-black uppercase tracking-wider text-text-subtle-light dark:text-text-subtle-dark">
                        {t('customerManagement.plan')}
                      </p>
                      <p className="text-sm font-bold text-slate-700 dark:text-slate-300 truncate">
                        {!c.plan_name || c.plan_name.toLowerCase() === 'no plan'
                          ? t('customerManagement.noPlan')
                          : c.plan_name}
                      </p>
                      <p className="text-xs text-text-muted-light dark:text-text-muted-dark">
                        {new Intl.NumberFormat(locale, {
                          style: 'currency',
                          currency: 'USD',
                        }).format(c.plan_amount / 100)}
                        {t('customerManagement.perMonth')}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-wider text-text-subtle-light dark:text-text-subtle-dark mb-1">
                        {t('customerManagement.status')}
                      </p>
                      <Badge variant={toneToBadgeVariant[getStatusTone(c.status)]}>
                        {t(`customerManagement.statuses.${c.status.toLowerCase()}`, {
                          defaultValue: c.status,
                        })}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-bold text-text-muted-light dark:text-text-muted-dark">
                    <span className="flex items-center gap-1">
                      <HardDrive size={14} />{' '}
                      {new Intl.NumberFormat(locale, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(c.storage_used_gb)}{' '}
                      GB
                    </span>
                    <span className="flex items-center gap-1">
                      <Database size={14} />{' '}
                      {t('customerManagement.backups', { count: c.backup_count })}
                    </span>
                    <span>{new Date(c.created_at).toLocaleDateString(locale)}</span>
                  </div>
                </Card>
              ))}
            </div>

            <div className="hidden lg:block bg-surface-card-light dark:bg-surface-card-dark rounded-2xl shadow-sm border border-surface-border-light dark:border-surface-border-dark overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-surface-border-light dark:border-surface-border-dark bg-surface-2-light dark:bg-surface-2-dark">
                      <th className="p-4 text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark">
                        {t('customerManagement.customer')}
                      </th>
                      <th className="p-4 text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark">
                        {t('customerManagement.plan')}
                      </th>
                      <th className="p-4 text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark">
                        {t('customerManagement.status')}
                      </th>
                      <th className="p-4 text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark">
                        {t('customerManagement.usage')}
                      </th>
                      <th className="p-4 text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark">
                        {t('customerManagement.joined')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-border-light dark:divide-surface-border-dark">
                    {customers.map((c) => (
                      <tr
                        key={c.id}
                        onClick={() => navigate(`/admin/customers/${c.id}`)}
                        className="hover:bg-surface-2-light dark:hover:bg-surface-2-dark cursor-pointer transition-colors group"
                      >
                        <td className="p-4">
                          <Link
                            to={`/admin/customers/${c.id}`}
                            onClick={(event) => event.stopPropagation()}
                            className="font-bold text-slate-900 dark:text-white group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded-sm"
                          >
                            {c.name}
                          </Link>
                          <div className="text-xs text-text-muted-light dark:text-text-muted-dark font-medium">
                            {c.billing_email}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="text-sm font-bold text-slate-700 dark:text-slate-300">
                            {!c.plan_name || c.plan_name.toLowerCase() === 'no plan'
                              ? t('customerManagement.noPlan')
                              : c.plan_name}
                          </div>
                          <div className="text-xs text-text-muted-light dark:text-text-muted-dark font-bold">
                            {new Intl.NumberFormat(locale, {
                              style: 'currency',
                              currency: 'USD',
                            }).format(c.plan_amount / 100)}
                            {t('customerManagement.perMonth')}
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge variant={toneToBadgeVariant[getStatusTone(c.status)]}>
                            {t(`customerManagement.statuses.${c.status.toLowerCase()}`, {
                              defaultValue: c.status,
                            })}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-4 text-xs font-bold text-text-muted-light dark:text-text-muted-dark">
                            <span className="flex items-center gap-1">
                              <HardDrive size={12} />{' '}
                              {new Intl.NumberFormat(locale, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(c.storage_used_gb)}{' '}
                              GB
                            </span>
                            <span className="flex items-center gap-1">
                              <Database size={12} />{' '}
                              {new Intl.NumberFormat(locale).format(c.backup_count)}
                            </span>
                          </div>
                        </td>
                        <td className="p-4 text-sm font-medium text-text-muted-light dark:text-text-muted-dark">
                          {new Date(c.created_at).toLocaleDateString(locale)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-between items-center px-4">
              <p className="text-sm font-bold text-text-muted-light dark:text-text-muted-dark">
                {t('customerManagement.showingRange', {
                  from: new Intl.NumberFormat(locale).format(
                    (pagination.page - 1) * pagination.limit + 1
                  ),
                  to: new Intl.NumberFormat(locale).format(
                    Math.min(pagination.page * pagination.limit, pagination.total)
                  ),
                  total: new Intl.NumberFormat(locale).format(pagination.total),
                })}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft size={16} /> {t('customerManagement.previous')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={page === pagination.totalPages}
                >
                  {t('customerManagement.next')} <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
